import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, LoadingSkeleton } from '@nearrme/ui-web';
import { getLoginOptions, updateLoginOptions, type LoginOptionsState } from '../api';

const OPTION_COPY: Array<{
  key: keyof LoginOptionsState;
  title: string;
  description: string;
  locked?: boolean;
}> = [
  {
    key: 'emailPasswordEnabled',
    title: 'Email and password',
    description: 'Standard login for consumer accounts, Nearrme team, companies, and stores.',
  },
  {
    key: 'emailCodeEnabled',
    title: 'Login with code sent to email',
    description: 'Passwordless email code login. Enabled for everyone while SMS is being finalized.',
  },
  {
    key: 'phoneCodeEnabled',
    title: 'Login with SMS code',
    description: 'Kept off until the India SMS provider, DLT sender, entity, and templates are configured.',
    locked: true,
  },
  {
    key: 'googleEnabled',
    title: 'Google sign-in',
    description: 'Planned for consumer and business signup/login after Google client IDs are configured.',
    locked: true,
  },
  {
    key: 'passkeyEnabled',
    title: 'Passkeys',
    description: 'Planned after account recovery, device registration, and audit policies are finalized.',
    locked: true,
  },
];

export function AdminLoginOptionsPage() {
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ['admin', 'login-options'], queryFn: getLoginOptions });
  const mutation = useMutation({
    mutationFn: updateLoginOptions,
    onSuccess: (data) => queryClient.setQueryData(['admin', 'login-options'], data),
  });

  const updateOption = (key: keyof LoginOptionsState, value: boolean) => {
    if (!options.data) return;
    mutation.mutate({ ...options.data, [key]: value });
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Login options</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Control which sign-in methods are available across consumer, Nearrme team, company, and store accounts.
      </p>

      {options.isLoading && (
        <div className="mt-6 grid gap-3">
          {[1, 2, 3].map((item) => <LoadingSkeleton key={item} className="h-24 w-full" />)}
        </div>
      )}

      {options.error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(options.error as Error).message}</div>
        </Card>
      )}

      {options.data && (
        <div className="mt-6 grid gap-3">
          {OPTION_COPY.map((option) => {
            const enabled = Boolean(options.data[option.key]);
            return (
              <Card key={option.key} padded>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">{option.title}</h2>
                      {option.locked && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Not ready
                        </span>
                      )}
                    </div>
                    <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">{option.description}</p>
                  </div>
                  <label className="inline-flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={option.locked || mutation.isPending}
                      onChange={(e) => updateOption(option.key, e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-40"
                    />
                  </label>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card padded className="mt-6">
        <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Current rollout</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
          Email/password and email-code login are enabled for everyone. SMS login remains off until the India provider credentials and DLT templates are confirmed, because failed or non-compliant OTP delivery will block users.
        </p>
        {mutation.error && (
          <div className="mt-3 text-sm text-red-600 dark:text-red-400">{(mutation.error as Error).message}</div>
        )}
      </Card>
    </div>
  );
}
